'use client';

import { IconButton, TextField, Label, FormControl } from '@backyard-ui/core';
import { MinusCircleOutline, PlusCircleOutline } from '@backyard-ui/icons';

import { styles } from './Counter.styles';
import useCounter from './useCounter';

export type CounterAction = 'increment' | 'decrement';
export type CounterEvent = 'click' | 'inputChange';

export type OnChangeCounter = (props: {
  /**
   * Action performed against the updated quantity.
   */
  action: CounterAction;

  /**
   * Event that dispatched this update.
   */
  event: CounterEvent;

  /**
   * Original Quantity.
   */
  quantity: number;

  /**
   * Quantity divided by multiplier.
   */
  unitQuantity: number;
}) => void;

export interface CounterProps {
  /**
   * Initial quantity.
   */
  quantity?: number;

  /**
   * Limit to which the value can be increased.
   */
  maxQuantity?: number;

  /**
   * Multiplier applied in increase or decrease interation.
   */
  multiplier?: number;

  /**
   * Event that dispatches every time the quantity changes.
   */
  onChange: OnChangeCounter;

  /**
   * Event that dispatches every click event triggered.
   */
  onClick?: () => void;
}

const Counter = (props: Readonly<CounterProps>) => {
  const { onClick } = props;

  const classNames = styles();

  const {
    value,
    inputRef,
    canDecrease,
    canIncrease,
    handleDecrement,
    handleIncrement,
    handleInputChange,
    handleInputKeyDown,
    handleInputBlur,
  } = useCounter(props);

  return (
    <FormControl.Root>
      <Label>Quantidade</Label>
      <TextField.Root UNSAFE_className={classNames.base()}>
        <TextField.Slot UNSAFE_className={classNames.button()}>
          <IconButton
            UNSAFE_className={classNames.icon()}
            aria-label="Diminuir"
            name="minus"
            variant="ghost"
            size="md"
            icon={<MinusCircleOutline />}
            onClick={() => {
              onClick && onClick();
              handleDecrement();
            }}
            disabled={!canDecrease}
          />
        </TextField.Slot>

        <TextField.Input
          ref={inputRef}
          UNSAFE_className={classNames.input()}
          type="number"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={onClick}
          onBlur={handleInputBlur}
        />

        <TextField.Slot UNSAFE_className={classNames.button()}>
          <IconButton
            UNSAFE_className={classNames.icon()}
            aria-label="Aumentar"
            name="plus"
            variant="ghost"
            size="md"
            icon={<PlusCircleOutline />}
            onClick={() => {
              onClick && onClick();
              handleIncrement();
            }}
            disabled={!canIncrease}
          />
        </TextField.Slot>
      </TextField.Root>
    </FormControl.Root>
  );
};

export default Counter;
